import React from 'react';
import { Table, Th, Td, Status } from '../ShelfPage/styles';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import RetailersFilters from './RetailersFilters';

export interface RetailersViewProps {
  search?: string;
  selectedCountries?: string[];
  selectedIndustries?: string[];
  onOptionsChange?: (opts: { countries: { id: string; label: string }[]; industries: { id: string; label: string }[] }) => void;
  showInlineFilters?: boolean;
  onSearchChange?: (v: string) => void;
  onToggleCountry?: (id: string) => void;
  onToggleIndustry?: (id: string) => void;
}

const RetailersView: React.FC<RetailersViewProps> = ({
  search: controlledSearch,
  selectedCountries: controlledCountries,
  selectedIndustries: controlledIndustries,
  onOptionsChange,
  showInlineFilters = true,
  onSearchChange,
  onToggleCountry,
  onToggleIndustry
}) => {
  const [activeRetailerIds, setActiveRetailerIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const path = buildLlmCrawlerPath('active-templates/retailers');
        const { data } = await axios.get(path, { params: { templateType: 'pricing', env: 'dev' } });
        const arr = Array.isArray(data?.activeRetailerIds)
          ? data.activeRetailerIds
          : Array.isArray(data?.retailerIds)
          ? data.retailerIds
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];
        const set = new Set<string>((arr as any[]).map((v) => String((v && (v.retailerId ?? v.id)) ?? v)));
        if (mounted) setActiveRetailerIds(set);
      } catch (_e) {
        if (mounted) setActiveRetailerIds(new Set());
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const [retailers, setRetailers] = React.useState<any[]>([]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const path = buildLlmCrawlerPath('retailers');
        const { data } = await axios.get(path);
        const list = Array.isArray(data?.retailers)
          ? data.retailers
          : [];
        if (mounted) setRetailers(list);
      } catch (_e) {
        if (mounted) setRetailers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Local state used only when rendering inline filters
  const [searchState, setSearchState] = React.useState('');
  const [selectedCountriesState, setSelectedCountriesState] = React.useState<string[]>([]);
  const [selectedIndustriesState, setSelectedIndustriesState] = React.useState<string[]>([]);

  // Determine effective filters (controlled props take priority)
  const effectiveSearch = controlledSearch ?? searchState;
  const effectiveCountries = controlledCountries ?? selectedCountriesState;
  const effectiveIndustries = controlledIndustries ?? selectedIndustriesState;

  const countryOptions = React.useMemo(() => {
    const set = new Set<string>();
    (retailers || []).forEach((r: any) => {
      const cc = (r?.countryCode || '').toString().toUpperCase();
      if (cc) set.add(cc);
    });
    return Array.from(set)
      .sort()
      .map((id) => ({ id, label: id }));
  }, [retailers]);

  const industryOptions = React.useMemo(() => {
    // Placeholder industry list for now
    const list = ['Online Marketplace'];
    return list.map((l) => ({ id: l.toLowerCase(), label: l }));
  }, []);

  // Notify parent when options change
  React.useEffect(() => {
    if (onOptionsChange) {
      onOptionsChange({ countries: countryOptions, industries: industryOptions });
    }
  }, [countryOptions, industryOptions, onOptionsChange]);

  const rows = React.useMemo(() => {
    const toIndustry = (_r: any): string => 'Online Marketplace';
    const toGeo = (r: any) => (r?.countryCode || '').toString().toUpperCase();

    return (retailers || [])
      .map((r: any) => ({
        id: String(r?.retailerId ?? r?.id ?? ''),
        retailer: r?.retailerName || r?.displayName || r?.name || '—',
        geo: toGeo(r),
        industry: toIndustry(r),
        status: 'Live'
      }))
      .filter((row) => row.id && activeRetailerIds.has(String(row.id)))
      .filter((row) => (effectiveSearch ? row.retailer.toLowerCase().includes(effectiveSearch.toLowerCase()) : true))
      .filter((row) => (effectiveCountries.length ? effectiveCountries.includes(row.geo) : true))
      .filter((row) => (effectiveIndustries.length ? effectiveIndustries.includes(row.industry.toLowerCase()) : true))
      .sort((a, b) => {
        const an = Number(a.id);
        const bn = Number(b.id);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return String(a.id).localeCompare(String(b.id));
      });
  }, [activeRetailerIds, retailers, effectiveSearch, effectiveCountries, effectiveIndustries]);

  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  const pagedRows = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page]);

  const onClickNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const onClickPrevious = () => setPage((p) => Math.max(1, p - 1));

  return (
    <div style={showInlineFilters ? { display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24 } : {}}>
      {showInlineFilters ? (
        <div>
          <RetailersFilters
            search={effectiveSearch}
            onSearchChange={(v) => (onSearchChange ? onSearchChange(v) : setSearchState(v))}
            availableCountries={countryOptions}
            selectedCountries={effectiveCountries}
            onToggleCountry={(id) =>
              onToggleCountry
                ? onToggleCountry(id)
                : setSelectedCountriesState((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
            }
            availableIndustries={industryOptions}
            selectedIndustries={effectiveIndustries}
            onToggleIndustry={(id) =>
              onToggleIndustry
                ? onToggleIndustry(id)
                : setSelectedIndustriesState((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
            }
          />
        </div>
      ) : null}
      <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 20, marginTop: 16 }}>Available Retailers</div>
        <div style={{ color: '#031425', fontSize: 14, marginTop: 6 }}>
          Explore our list of 1,000+ retailers and start collecting data today
        </div>
      </div>
      <div style={{ border: '1px solid #E6ECF2', borderRadius: 8, overflow: 'hidden', background: 'white' }}>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: '45%' }}>Retailer</Th>
              <Th style={{ width: '15%' }}>Geo</Th>
              <Th style={{ width: '25%' }}>Industry</Th>
              <Th style={{ width: '15%' }}>Status</Th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row) => (
              <tr key={row.id}>
                <Td>{row.retailer}</Td>
                <Td>{row.geo}</Td>
                <Td>{row.industry}</Td>
                <Td>
                  <Status variant={row.status === 'Live' ? 'enabled' : 'disabled'}>{row.status}</Status>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 32, paddingBottom: 16 }}>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onClickNext={onClickNext}
          onClickPrevious={onClickPrevious}
          shouldUseInfinitePagination
        />
      </div>
      </div>
    </div>
  );
};

export default RetailersView;


